import React, { useMemo, useState } from "react";
import { Box, Divider, MenuItem, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IPlaylistGroup } from "../../Api/Interface/api.interface";
import { useAppSelector } from "../../Store/hooks";
import { selectCanCreatePlaylistGroup } from "../../Store/Auth/AuthSelector";
import PlaylistGroupQuickCreateDialog from "../../Pages/PlaylistGroup/PlaylistGroupQuickCreateDialog";

/**
 * Shared playlist-group picker used on the Device View, Device Create and
 * Device Edit pages. Renders the standard select — "No Playlist Group", then
 * the active groups — and, for users allowed to create groups, a final
 * "+ Add New Playlist Group" action styled as an affordance (not a group).
 *
 * Selecting the action opens a quick-create dialog (same endpoint/validation
 * as the full Playlist Groups create page). On success the new group is
 * pre-selected here — but NOT assigned; the parent's own Apply/Save button
 * still drives the actual assignment, so page state and the device's current
 * assignment are preserved until the user commits.
 */

const ADD_NEW = "__add_new_playlist_group__";

interface PlaylistGroupSelectProps {
  /** Currently selected group id, or "" for "No Playlist Group". */
  value: number | "";
  /** Fires with the chosen id (or "") — never with the add-new sentinel. */
  onChange: (value: number | "") => void;
  /** Active groups to list (parent already filters by is_active). */
  groups: IPlaylistGroup[];
  /** sx forwarded to the underlying TextField (e.g. signageInputSx). */
  sx?: object;
  disabled?: boolean;
  /**
   * When set, the quick-create dialog opens with this company already
   * selected. Device View / Create / Edit pages pass the device's own
   * `company_id` so a newly-created group inherits the same tenant as
   * the device — no manual re-picking, no accidental cross-tenant
   * assignment. Never derived from company NAME — always from id.
   */
  createInCompanyId?: number | null;
}

const PlaylistGroupSelect: React.FC<PlaylistGroupSelectProps> = ({
  value, onChange, groups, sx, disabled, createInCompanyId,
}) => {
  const canCreate = useAppSelector(selectCanCreatePlaylistGroup);
  const [dialogOpen, setDialogOpen] = useState(false);
  // A just-created group is kept locally so it can be shown and stay selected
  // even before the invalidated groups query finishes refetching.
  const [justCreated, setJustCreated] = useState<IPlaylistGroup | null>(null);

  const options = useMemo(() => {
    if (justCreated && !groups.some((g) => g.id === justCreated.id)) {
      return [...groups, justCreated];
    }
    return groups;
  }, [groups, justCreated]);

  const handleChange = (raw: string) => {
    if (raw === ADD_NEW) {
      setDialogOpen(true);
      return; // Don't change the current selection while the dialog is open.
    }
    onChange(raw === "" ? "" : Number(raw));
  };

  const handleCreated = (group: IPlaylistGroup) => {
    // Pre-select the new group without assigning it — the parent's Apply/Save
    // button is still what commits the change.
    setJustCreated(group);
    onChange(group.id);
  };

  return (
    <>
      <TextField
        select fullWidth size="small"
        value={value === "" ? "" : String(value)}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        sx={sx}
      >
        <MenuItem value="">— No Playlist Group —</MenuItem>
        {options.map((g) => (
          <MenuItem key={g.id} value={String(g.id)}>
            {g.name} · {g.playlists_count ?? 0} playlists · {g.playback_mode}
          </MenuItem>
        ))}
        {canCreate && <Divider sx={{ my: 0.5 }} />}
        {canCreate && (
          <MenuItem
            value={ADD_NEW}
            sx={{
              py: 1.1,
              color: "#7c3aed",
              fontWeight: 700,
              "&:hover": { bgcolor: "rgba(124,58,237,0.08)" },
              "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "transparent" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{
                width: 20, height: 20, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(124,58,237,0.12)",
              }}>
                <AddIcon sx={{ fontSize: 15, color: "#7c3aed" }} />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>
                Add New Playlist Group
              </Typography>
            </Box>
          </MenuItem>
        )}
      </TextField>

      <PlaylistGroupQuickCreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
        initialCompanyId={createInCompanyId ?? null}
      />
    </>
  );
};

export default PlaylistGroupSelect;
